// Workstream: accounts + photo cards. The "Photo cards" section of the pack editor.
import { useRef, useState } from 'react';
import { CARD_TEXT_MAX } from '@shared/rules';
import type { CardData, CardInput, CardType } from '@shared/types';
import { imageIdFromUrl, resizePhoto } from './photos';

export const PHOTO_CARDS_MAX = 60;

/** Default prompts for photo cards, so several photos don't all say the same thing. */
const PHOTO_PROMPTS = [
  'Caption this: ___',
  'What is really going on in this photo? ___',
  'The WhatsApp caption this photo deserves: ___',
  'Nobody in this photo knows about ___.',
  'Dadi looks at this photo and says: ___',
  'Minutes before this photo: ___',
];

export interface PhotoDraft {
  key: string;
  /** Existing card id, so edits keep play stats. */
  id?: string;
  type: CardType;
  text: string;
  /** Set once the photo is on the server. */
  imageId: string | null;
  /** What we show: the server URL, or a data: URL for a photo not uploaded yet (CSP blocks blob:). */
  preview: string;
  /** The resized JPEG waiting to be uploaded. */
  blob?: Blob;
  gloss?: string;
  explanation?: string;
}

let nextKey = 1;
const newKey = () => `photo-${nextKey++}`;

export function draftsFromCards(cards: CardData[]): PhotoDraft[] {
  return cards
    .filter((c) => c.imageUrl)
    .map((c) => ({
      key: newKey(),
      id: c.id,
      type: c.type,
      text: c.text,
      imageId: imageIdFromUrl(c.imageUrl),
      preview: c.imageUrl!,
      gloss: c.gloss,
      explanation: c.explanation,
    }));
}

export function draftsToCards(drafts: PhotoDraft[]): CardInput[] {
  return drafts.map((d) => ({
    id: d.id,
    type: d.type,
    text: d.text.trim(),
    gloss: d.gloss ?? '',
    explanation: d.explanation ?? '',
    imageId: d.imageId,
  }));
}

/** A reason the photo cards can't be saved yet, or null. `otherTexts` are the text cards' "type|text" keys. */
export function photoDraftProblem(drafts: PhotoDraft[], otherTexts: Set<string>): string | null {
  const seen = new Set(otherTexts);
  for (const d of drafts) {
    const text = d.text.trim();
    if (!text) return d.type === 'answer' ? 'Give every photo answer a short caption, like "Chintu at his mundan".' : 'Every photo prompt needs some text.';
    if (text.length > CARD_TEXT_MAX) return `Photo card text is too long (max ${CARD_TEXT_MAX} characters).`;
    const k = `${d.type}|${text.toLowerCase()}`;
    if (seen.has(k)) return `Two cards say "${text}". Change one so both photos are kept.`;
    seen.add(k);
  }
  return null;
}

function readAsDataUrl(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(new Error('Could not read that photo.'));
    reader.readAsDataURL(blob);
  });
}

interface Props {
  drafts: PhotoDraft[];
  onChange: (update: (drafts: PhotoDraft[]) => PhotoDraft[]) => void;
  disabled?: boolean;
}

export function PhotoCardsEditor({ drafts, onChange, disabled }: Props) {
  const input = useRef<HTMLInputElement>(null);
  const [adding, setAdding] = useState<CardType>('prompt');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const focusKey = useRef<string | null>(null);

  function pick(type: CardType) {
    setAdding(type);
    setError('');
    input.current?.click();
  }

  async function addFiles(files: FileList | null) {
    if (!files?.length) return;
    const room = PHOTO_CARDS_MAX - drafts.length;
    if (room <= 0) return setError(`A pack can have up to ${PHOTO_CARDS_MAX} photo cards.`);
    setBusy(true);
    setError('');
    const added: PhotoDraft[] = [];
    try {
      for (const file of [...files].slice(0, room)) {
        const blob = await resizePhoto(file);
        added.push({ key: newKey(), type: adding, text: '', imageId: null, preview: await readAsDataUrl(blob), blob });
      }
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
      if (input.current) input.current.value = '';
    }
    if (!added.length) return;
    onChange((current) => {
      const used = new Set(current.map((d) => d.text.trim().toLowerCase()));
      const named = added.map((d) => {
        if (d.type !== 'prompt') return d;
        const text = PHOTO_PROMPTS.find((p) => !used.has(p.toLowerCase())) ?? `Caption photo ${used.size + 1}: ___`;
        used.add(text.toLowerCase());
        return { ...d, text };
      });
      return [...current, ...named];
    });
    // Answers need a caption, so put the cursor straight in the first new one.
    focusKey.current = added[0].type === 'answer' ? added[0].key : null;
  }

  const update = (key: string, text: string) => onChange((ds) => ds.map((d) => (d.key === key ? { ...d, text } : d)));
  const remove = (key: string) => onChange((ds) => ds.filter((d) => d.key !== key));

  return (
    <div className="panel stack photo-cards">
      <div>
        <h2 style={{ margin: 0 }}>
          Photo cards <span className="muted">({drafts.length})</span>
        </h2>
        <p className="muted small" style={{ margin: '4px 0 0' }}>
          Old wedding photos, Chacha's passport picture, the dog in a lehenga. A photo prompt asks everyone to caption it. A photo answer
          is a card players can play. Photos are shrunk on your phone before they upload.
        </p>
      </div>
      {drafts.length > 0 && (
        <ul className="photo-grid">
          {drafts.map((d) => (
            <li key={d.key} className={`photo-tile ${d.type}`}>
              <img src={d.preview} alt="" />
              <span className={`chip ${d.type === 'prompt' ? 'blue' : 'orange'}`}>{d.type === 'prompt' ? 'Prompt' : 'Answer'}</span>
              <label className="sr-only" htmlFor={`${d.key}-text`}>
                {d.type === 'prompt' ? 'Prompt text' : 'Caption'}
              </label>
              <textarea
                id={`${d.key}-text`}
                className="input"
                rows={3}
                value={d.text}
                maxLength={CARD_TEXT_MAX}
                placeholder={d.type === 'prompt' ? 'Caption this: ___' : 'Who or what is this?'}
                onChange={(e) => update(d.key, e.target.value)}
                ref={(el) => {
                  if (el && focusKey.current === d.key) {
                    focusKey.current = null;
                    el.focus();
                  }
                }}
                disabled={disabled}
              />
              <button type="button" className="photo-remove" onClick={() => remove(d.key)} aria-label="Remove this photo card" disabled={disabled}>
                ✕
              </button>
              {!d.imageId && <span className="photo-pending">Uploads when you save</span>}
            </li>
          ))}
        </ul>
      )}
      <input
        ref={input}
        type="file"
        accept="image/jpeg,image/png,image/webp,image/heic,image/heif,image/*"
        multiple
        hidden
        onChange={(e) => void addFiles(e.target.files)}
      />
      <div className="row">
        <button type="button" className="btn btn-outline grow" onClick={() => pick('prompt')} disabled={busy || disabled}>
          📷 Add photo prompt
        </button>
        <button type="button" className="btn btn-outline grow" onClick={() => pick('answer')} disabled={busy || disabled}>
          📷 Add photo answer
        </button>
      </div>
      {busy && <p className="muted small" style={{ margin: 0 }}>Shrinking your photo...</p>}
      {error && (
        <p className="error" role="alert" style={{ margin: 0 }}>
          {error}
        </p>
      )}
    </div>
  );
}
